import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, Monitor, Smartphone, ChevronDown } from 'lucide-react';
import { useAuth } from '@/hooks/useAuthApi';
import { useOrganization } from '@/hooks/useOrganization';
import { useSessionsList } from '@/hooks/sessions/useSessionsList';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { QuickSuggestionPills } from '@/components/dashboard/QuickSuggestionPills';
import { SessionsChart, type DemoChartDataPoint } from '@/components/dashboard/DemosChart';
import { SessionsTable, type DemoTableRow } from '@/components/dashboard/DemosTable';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/layout/PageHeader';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DataSurface } from '@/components/platform/DataSurface';
import { MetricStrip } from '@/components/platform/MetricStrip';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.round(seconds / 60);
  return `${minutes}m`;
};

const DATE_PRESETS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
] as const;

type DatePresetValue = (typeof DATE_PRESETS)[number]['value'];

const toMetricTrend = (raw: number | null | undefined): { value: string; tone: 'positive' | 'negative' | 'neutral' } | undefined => {
  if (raw == null) return undefined;
  if (raw === 0) return { value: '0%', tone: 'neutral' };
  return {
    value: `${raw > 0 ? '+' : ''}${raw}%`,
    tone: raw > 0 ? 'positive' : 'negative',
  };
};

// Chart colors — derived from CSS vars at render time for theme support
const DEVICE_COLORS = [
  'hsl(var(--foreground))',          // desktop — primary (near-black / near-white)
  'hsl(var(--muted-foreground))',    // mobile — muted
];

const SOURCE_COLORS = [
  'hsl(var(--foreground))',          // primary source
  'hsl(var(--muted-foreground))',    // secondary
  'hsl(var(--border))',              // tertiary
  'hsl(var(--muted-foreground) / 0.5)', // quaternary
];

const MiniDonut: React.FC<{
  segments: { value: number; color: string }[];
  size?: number;
  strokeWidth?: number;
  centerLabel?: string;
}> = ({ segments, size = 80, strokeWidth = 10, centerLabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;

  let accumulated = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
      <circle
        cx={center} cy={center} r={radius}
        fill="none" strokeWidth={strokeWidth}
        stroke="hsl(var(--muted))"
      />
      {segments.map((seg, i) => {
        const segLen = (seg.value / total) * circumference;
        const offset = accumulated;
        accumulated += segLen;
        if (seg.value === 0) return null;
        return (
          <circle
            key={i}
            cx={center} cy={center} r={radius}
            fill="none" strokeWidth={strokeWidth}
            stroke={seg.color}
            strokeDasharray={`${segLen} ${circumference - segLen}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${center} ${center})`}
            style={{ transition: 'stroke-dasharray 0.3s ease' }}
          />
        );
      })}
      {centerLabel && (
        <text
          x={center} y={center}
          textAnchor="middle" dominantBaseline="central"
          fill="hsl(var(--muted-foreground))"
          fontSize="11" fontWeight="500"
        >
          {centerLabel}
        </text>
      )}
    </svg>
  );
};

const DashboardPage: React.FC = () => {
  const { userData } = useAuth();
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [selectedDays, setSelectedDays] = useState<DatePresetValue>(30);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const selectedPreset = DATE_PRESETS.find(p => p.value === selectedDays)!;
  const comparisonLabel = `vs prev ${selectedDays}d`;

  // Fetch sessions for chart and table
  const { sessions, isLoading: isLoadingSessions } = useSessionsList(
    currentOrganization?.id,
    { limit: 100 }
  );

  // Fetch dashboard metrics from the new endpoint
  const { data: dashboardMetrics, isLoading: isLoadingMetrics } = useDashboardMetrics(
    currentOrganization?.id,
    { days: selectedDays }
  );

  const firstName = useMemo(() => {
    const displayName = userData?.displayName?.trim();
    if (displayName) {
      const name = displayName.split(' ')[0];
      if (name) return name.charAt(0).toUpperCase() + name.slice(1);
    }
    const email = userData?.email;
    if (email) {
      const localPart = email.split('@')[0];
      if (localPart) return localPart.charAt(0).toUpperCase() + localPart.slice(1);
    }
    return null;
  }, [userData?.displayName, userData?.email]);

  // Generate chart data by aggregating sessions by date
  const chartData: DemoChartDataPoint[] = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];

    const dateMap = new Map<string, { total: number; qualified: number; booked: number; notBooked: number }>();

    sessions.forEach(session => {
      const dateStr = session.created_at
        ? new Date(session.created_at).toISOString().split('T')[0]
        : null;
      if (!dateStr) return;

      const existing = dateMap.get(dateStr) || { total: 0, qualified: 0, booked: 0, notBooked: 0 };
      existing.total += 1;
      
      const qualStatus = session.qualification_status?.toLowerCase();
      const bookStatus = session.booked_status?.toLowerCase();

      if (qualStatus === 'qualified' || bookStatus === 'booked') {
        existing.qualified += 1;
      }

      if (bookStatus === 'booked') {
        existing.booked += 1;
      } else {
        existing.notBooked += 1;
      }
      dateMap.set(dateStr, existing);
    });

    return Array.from(dateMap.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [sessions]);

  // Transform sessions to table rows (sorted by most recent first)
  const tableData: DemoTableRow[] = useMemo(() => {
    if (!sessions) return [];

    return sessions
      .map(session => ({
        id: session.session_recording_id,
        email: session.email || 'Anonymous',
        qualification_status: session.qualification_status,
        booked_status: session.booked_status,
        role: session.decision_maker_role,
        date: session.created_at,
        duration: session.duration_seconds,
      }))
      .sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
  }, [sessions]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && prompt.trim()) {
      e.preventDefault();
      navigate(`/ask-hobbes?prompt=${encodeURIComponent(prompt.trim())}`);
    }
  };

  const handleSubmit = () => {
    if (prompt.trim()) {
      navigate(`/ask-hobbes?prompt=${encodeURIComponent(prompt.trim())}`);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    navigate(`/ask-hobbes?prompt=${encodeURIComponent(suggestion)}`);
  };

  // Pre-compute device breakdown
  const deviceBreakdown = useMemo(() => {
    const desktop = dashboardMetrics?.desktopCount ?? 0;
    const mobile = dashboardMetrics?.mobileCount ?? 0;
    const total = desktop + mobile || 1;
    return {
      desktop,
      mobile,
      total,
      desktopPct: Math.round((desktop / total) * 100),
      mobilePct: Math.round((mobile / total) * 100),
    };
  }, [dashboardMetrics?.desktopCount, dashboardMetrics?.mobileCount]);

  // Pre-compute source breakdown
  const sourceBreakdown = useMemo(() => {
    const sources = dashboardMetrics?.topSources ?? [];
    const maxCount = sources.length > 0 ? sources[0].count : 1;
    return { sources, maxCount };
  }, [dashboardMetrics?.topSources]);

  const isLoading = isLoadingSessions || isLoadingMetrics;

  if (!currentOrganization?.id) {
    return <div className="p-4 text-center">Select an organization to view the dashboard.</div>;
  }

  return (
    <>
      <Helmet>
        <title>Home | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
      <PageHeader title="Home" titleTooltip="Your activity overview and Ask Hobbes entry point." />

      {/* Scrollable Content */}
      <div className="flex-1 min-w-0 px-4 sm:px-6 md:px-8 py-5">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {firstName ? `${getGreeting()}, ${firstName}` : getGreeting()}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Ask Hobbes or review recent activity.
            </p>
          </div>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 shrink-0 justify-between rounded-md border border-border bg-background px-3 text-sm text-muted-foreground shadow-none hover:bg-accent hover:text-accent-foreground"
              >
                <span className="truncate text-left">{selectedPreset.label}</span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[180px] p-2" align="end">
              <div className="flex flex-col gap-1">
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    className={`flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent ${
                      selectedDays === preset.value ? 'font-medium bg-accent' : ''
                    }`}
                    onClick={() => {
                      setSelectedDays(preset.value);
                      setDatePickerOpen(false);
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Ask Hobbes Section */}
        <div className="mb-6">
          {/* Ask Hobbes Input Card */}
          <DataSurface className="mb-3 bg-background">
            <div className="px-4 pt-4 pb-2">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Hobbes anything..."
                className="min-h-[60px] resize-none border-0 bg-transparent text-sm shadow-none focus-visible:ring-0"
                rows={2}
              />
            </div>
            <div className="px-4 pb-3 flex items-center justify-end border-t border-border/70 pt-3">
              <Button
                type="button"
                size="icon"
                onClick={handleSubmit}
                disabled={!prompt.trim()}
                className="h-[30px] w-[30px] rounded-full"
              >
                <ArrowUp size={16} />
              </Button>
            </div>
          </DataSurface>
          <QuickSuggestionPills onSuggestionClick={handleSuggestionClick} />
        </div>

        {/* Overview Section */}
        <div className="mb-6 @container">
          {isLoading ? (
            <DataSurface className="grid sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="border-border/70 px-4 py-3 [&:not(:first-child)]:border-t sm:[&:nth-child(even)]:border-l sm:[&:nth-child(even)]:border-t-0 lg:[&:not(:first-child)]:border-l lg:[&:not(:first-child)]:border-t-0">
                  <Skeleton className="mb-3 h-3 w-24" />
                  <Skeleton className="mb-2 h-7 w-16" />
                  <Skeleton className="h-3 w-28" />
                </div>
              ))}
            </DataSurface>
          ) : (
            <MetricStrip
              items={[
                {
                  label: 'Sessions',
                  value: (dashboardMetrics?.totalDemos ?? 0).toLocaleString(),
                  meta: comparisonLabel,
                  trend: toMetricTrend(dashboardMetrics?.totalDemosChange),
                },
                {
                  label: 'Qualified',
                  value: `${dashboardMetrics?.qualificationRate ?? 0}%`,
                  meta: `${dashboardMetrics?.qualifiedCount ?? 0} qualified · ${dashboardMetrics?.unqualifiedCount ?? 0} unqualified`,
                  trend: toMetricTrend(dashboardMetrics?.qualificationRateChange),
                },
                {
                  label: 'Avg length',
                  value: formatDuration(dashboardMetrics?.avgSessionLengthSeconds ?? 0),
                  meta: selectedPreset.label,
                  trend: toMetricTrend(dashboardMetrics?.avgSessionLengthChange),
                },
                {
                  label: 'High intent',
                  value: (dashboardMetrics?.highIntentLeads ?? 0).toLocaleString(),
                  meta: 'Strong buying signals',
                  trend: toMetricTrend(dashboardMetrics?.highIntentLeadsChange),
                },
              ]}
            />
          )}
        </div>

        {/* Breakdown Cards */}
        {!isLoading && dashboardMetrics && (
          <div className="@container mb-6">
          <div className="grid grid-cols-1 @lg:grid-cols-2 gap-3">
            {/* Device Breakdown */}
            <DataSurface>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">Device breakdown</span>
                    <span className="text-xs text-muted-foreground">{selectedPreset.label}</span>
                  </div>
                </div>
                <div className="flex items-center gap-5 mt-4">
                  <MiniDonut
                    centerLabel={deviceBreakdown.total > 1 ? deviceBreakdown.total.toLocaleString() : undefined}
                    segments={[
                      { value: deviceBreakdown.desktop, color: DEVICE_COLORS[0] },
                      { value: deviceBreakdown.mobile, color: DEVICE_COLORS[1] },
                    ]}
                  />
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: DEVICE_COLORS[0] }} />
                        <Monitor className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm text-foreground">Desktop</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{deviceBreakdown.desktop.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">{deviceBreakdown.desktopPct}%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: DEVICE_COLORS[1] }} />
                        <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm text-foreground">Mobile</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{deviceBreakdown.mobile.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">{deviceBreakdown.mobilePct}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </DataSurface>

            {/* Source Breakdown */}
            <DataSurface>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">Top sources</span>
                    <span className="text-xs text-muted-foreground">{selectedPreset.label}</span>
                  </div>
                </div>
                {sourceBreakdown.sources.length === 0 ? (
                  <p className="text-sm text-muted-foreground mt-4">No traffic sources detected yet. Sources appear once visitors start taking sessions.</p>
                ) : (
                  <div className="flex items-center gap-5 mt-4">
                    <MiniDonut
                      segments={sourceBreakdown.sources.map((src, i) => ({
                        value: src.count,
                        color: SOURCE_COLORS[i] || SOURCE_COLORS[SOURCE_COLORS.length - 1],
                      }))}
                    />
                    <div className="flex-1 space-y-3">
                      {sourceBreakdown.sources.map((src, i) => (
                        <div key={src.source} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: SOURCE_COLORS[i] || SOURCE_COLORS[SOURCE_COLORS.length - 1] }}
                            />
                            <span className="text-sm text-foreground capitalize">{src.source}</span>
                          </div>
                          <span className="text-sm font-medium text-foreground">{src.count.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </DataSurface>
          </div>
          </div>
        )}

        {/* Chart Section */}
        <div className="mb-6">
          <SessionsChart data={chartData} />
        </div>

        {/* Table Section */}
        <SessionsTable sessions={tableData} isLoading={isLoadingSessions} />
      </div>
    </div>
    </>
  );
};

export default DashboardPage;
